import { format, isValid, parse } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * The wire format `<input type="datetime-local">` used, kept verbatim.
 *
 * Callers still hold a plain `YYYY-MM-DDTHH:mm` string and an empty one still
 * means "unset" — so this is a drop-in for the native control, and the two
 * screens using it did not have to learn a `Date`.
 */
const WIRE = "yyyy-MM-dd'T'HH:mm";

interface DateTimePickerProps {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  /** Shown on the trigger when nothing is set. */
  readonly placeholder?: string;
  readonly className?: string;
}

/**
 * Date and time in one control: a calendar for the day, a time field for the
 * clock, and a clear button.
 *
 * It replaces `<input type="datetime-local">`, which rendered as whatever the
 * operating system felt like — a different size, font and interaction on
 * every machine, and on Firefox barely a picker at all. This one is the
 * console's own, so the two places that schedule something look like the rest
 * of the console.
 */
export function DateTimePicker({
  id,
  value,
  onChange,
  disabled = false,
  placeholder = 'Pick a date',
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);

  const parsed = parse(value, WIRE, new Date());
  const selected = value !== '' && isValid(parsed) ? parsed : undefined;
  const time = selected === undefined ? '' : format(selected, 'HH:mm');

  /** Keeps the clock reading while the day moves, and vice versa. */
  function commit(day: Date | undefined, clock: string): void {
    if (day === undefined) {
      onChange('');
      return;
    }

    const [hours, minutes] = clock.split(':');
    const next = new Date(day);
    next.setHours(Number(hours ?? 0), Number(minutes ?? 0), 0, 0);
    onChange(format(next, WIRE));
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn('justify-start font-normal', selected === undefined && 'text-muted-foreground')}
          >
            <CalendarIcon aria-hidden="true" />
            {selected === undefined ? placeholder : format(selected, 'd MMM yyyy, HH:mm')}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="flex flex-col gap-3">
          {/*
            Props spread rather than written inline: `exactOptionalPropertyTypes`
            is on, and react-day-picker declares `selected`/`defaultMonth`
            without `| undefined`, so passing an absent date literally is a
            type error even though it is the documented "nothing selected" case.
          */}
          <Calendar
            mode="single"
            autoFocus
            {...(selected === undefined ? {} : { selected, defaultMonth: selected })}
            onSelect={(day) => {
              // A day with no clock yet lands at 09:00 rather than midnight:
              // "expires today" almost never means the instant it began.
              commit(day, time === '' ? '09:00' : time);
            }}
          />

          <div className="flex items-center gap-2 border-t border-border pt-3">
            <Label htmlFor={`${id ?? 'datetime'}-time`} className="text-muted-foreground">
              Time
            </Label>
            <Input
              id={`${id ?? 'datetime'}-time`}
              type="time"
              value={time}
              disabled={selected === undefined}
              onChange={(event) => {
                commit(selected, event.target.value);
              }}
              className="w-32"
            />
          </div>
        </PopoverContent>
      </Popover>

      {selected === undefined || disabled ? null : (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Clear date"
          onClick={() => {
            onChange('');
          }}
        >
          <XIcon aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
